import React, { useState, useEffect } from 'react';
import { FiMail, FiBell, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {
  getNotificationPreferences,
  updateNotificationPreference,
  DEFAULT_NOTIFICATION_PREFERENCES,
} from '../services/notificationPreferencesService';
import { useToast } from '../contexts/ToastContext';
import { hapticSelection } from '../services/hapticsService';

/**
 * NotificationPreferencesSection
 *
 * Props (all optional, for push notifications on native):
 * - pushStatus: 'checking' | 'granted' | 'disabled' | 'denied' | 'prompt' | 'unavailable'
 * - pushLoading: boolean
 * - onEnablePush: () => void
 * - onDisablePush: () => void
 */
function NotificationPreferencesSection({
  pushStatus,
  pushLoading,
  onEnablePush,
  onDisablePush,
}) {
  const toast = useToast();
  const [preferences, setPreferences] = useState(DEFAULT_NOTIFICATION_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  // Determine if push toggle should be shown (native only, passed via props)
  const showPushToggle = pushStatus && pushStatus !== 'unavailable';

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      setLoading(true);
      try {
        const { data, error } = await getNotificationPreferences();
        if (error) {
          console.error('Error loading notification preferences:', error);
        } else {
          setPreferences(data);
        }
      } catch (error) {
        console.error('Exception loading notification preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, []);

  // Handle master toggle change (email only now)
  const handleMasterToggle = async (channel) => {
    const key = `${channel}_enabled`;
    const newValue = !preferences[key];

    // Optimistic update
    setPreferences(prev => ({ ...prev, [key]: newValue }));
    setSaving(prev => ({ ...prev, [key]: true }));

    try {
      const { error } = await updateNotificationPreference(key, newValue);
      if (error) {
        // Rollback on error
        setPreferences(prev => ({ ...prev, [key]: !newValue }));
        toast.error(`Failed to update ${channel} notifications`);
      }
    } catch (error) {
      setPreferences(prev => ({ ...prev, [key]: !newValue }));
      toast.error(`Failed to update ${channel} notifications`);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  // Render a toggle switch
  const Toggle = ({ enabled, onChange, saving: isSaving }) => {
    return (
      <button
        onClick={() => { hapticSelection(); onChange?.(); }}
        disabled={isSaving}
        className={`relative w-14 h-8 rounded-full transition-colors duration-200 disabled:opacity-50 ${
          enabled ? 'bg-accent' : 'bg-subtle'
        }`}
      >
        {isSaving ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <span
            className={`absolute w-6 h-6 top-1 left-1 bg-card rounded-full shadow transition-transform duration-200 ${
              enabled ? 'translate-x-6' : 'translate-x-0'
            }`}
          />
        )}
      </button>
    );
  };

  // Get push status display text
  const getPushStatusText = () => {
    switch (pushStatus) {
      case 'checking': return 'Checking...';
      case 'granted': return 'Push Notifications';
      case 'disabled': return 'Push Notifications';
      case 'denied': return 'Push Notifications Blocked';
      case 'prompt': return 'Push Notifications';
      default: return 'Push Notifications';
    }
  };

  const getPushSubtext = () => {
    switch (pushStatus) {
      case 'checking': return 'Checking permission status...';
      case 'granted': return 'Receiving push notifications';
      case 'disabled': return 'Push notifications disabled';
      case 'denied': return 'Enable in your device settings';
      case 'prompt': return 'Tap Enable to receive push notifications';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-5">Notifications</h3>
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-text-primary mb-5">Notifications</h3>

      <div className="space-y-4">
        {/* Email Toggle */}
        <div className="p-4 rounded-xl border-2 border-border-subtle bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                preferences.email_enabled ? 'bg-accent' : 'bg-surface'
              }`}>
                <SafeIcon icon={FiMail} className={`w-5 h-5 ${
                  preferences.email_enabled ? 'text-white' : 'text-text-muted'
                }`} />
              </div>
              <div>
                <p className={`font-semibold ${
                  preferences.email_enabled ? 'text-accent' : 'text-text-primary'
                }`}>
                  Email Notifications
                </p>
                <p className="text-xs text-text-muted">
                  {preferences.email_enabled ? 'Receiving email notifications' : 'Email notifications disabled'}
                </p>
              </div>
            </div>
            <Toggle
              enabled={preferences.email_enabled}
              onChange={() => handleMasterToggle('email')}
              saving={saving.email_enabled}
            />
          </div>
        </div>

        {/* Push Toggle - Only on native/iOS */}
        {showPushToggle && (
          <div className="p-4 rounded-xl border-2 border-border-subtle bg-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  pushStatus === 'granted' ? 'bg-accent' : 'bg-surface'
                }`}>
                  <SafeIcon icon={FiBell} className={`w-5 h-5 ${
                    pushStatus === 'granted' ? 'text-white' : 'text-text-muted'
                  }`} />
                </div>
                <div>
                  <p className={`font-semibold ${
                    pushStatus === 'granted' ? 'text-accent' : 'text-text-primary'
                  }`}>
                    {getPushStatusText()}
                  </p>
                  <p className="text-xs text-text-muted">
                    {getPushSubtext()}
                  </p>
                </div>
              </div>

              {/* Show enable button only when permission can be requested */}
              {pushStatus === 'prompt' && (
                <button
                  onClick={onEnablePush}
                  disabled={pushLoading}
                  className="px-4 py-2 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {pushLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Enabling...
                    </>
                  ) : (
                    'Enable'
                  )}
                </button>
              )}

              {/* Show toggle when permission is granted (enabled or disabled state) */}
              {(pushStatus === 'granted' || pushStatus === 'disabled') && (
                <button
                  onClick={pushStatus === 'granted' ? onDisablePush : onEnablePush}
                  disabled={pushLoading}
                  className={`relative w-14 h-8 rounded-full transition-colors duration-200 disabled:opacity-50 ${
                    pushStatus === 'granted' ? 'bg-accent' : 'bg-subtle'
                  }`}
                >
                  {pushLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <span
                      className={`absolute top-1 left-1 w-6 h-6 bg-card rounded-full shadow transition-transform duration-200 ${
                        pushStatus === 'granted' ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  )}
                </button>
              )}

              {/* Show alert for denied status */}
              {pushStatus === 'denied' && (
                <div className="w-10 h-10 rounded-full bg-status-warning/10 flex items-center justify-center">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-status-warning" />
                </div>
              )}
            </div>

            {/* Instructions for denied status */}
            {pushStatus === 'denied' && (
              <div className="mt-4 pt-4 border-t border-border-subtle">
                <p className="text-sm text-text-secondary">
                  To enable notifications, go to your device Settings &gt; TeeSync &gt; Notifications and turn them on.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disabled warning */}
      {!preferences.email_enabled && (
        <div className="mt-4 p-3 rounded-lg bg-status-warning/10 border border-status-warning/30">
          <p className="text-sm text-status-warning">
            Email notifications are disabled. You will only see in-app notifications.
          </p>
        </div>
      )}

    </div>
  );
}

export default NotificationPreferencesSection;
